import React, { useState, Fragment, ReactNode, useEffect } from "react";
import styled from "styled-components";
import { IconCheck20, IconCross20 } from "../icons-ts";
import { colors } from "../lib/constant";
import { Tooltip } from "./ui/Tooltip";
import { fontFamily, product_sans, fontSize } from "../lib/fonts";
import { Title } from "./ui/Title";
import { mediaQuery } from "react-styled-mediaquery";
import { motion } from "framer-motion";
import { ArrowLink } from "./ui/ArrowLink";
import { Link } from "react-scroll";

export interface TablePriceType {
  key: string;
  title: string;
  dossier: string;
  prixDossier: string;
  prixMois: string;
}

export interface TablePriceRow {
  title?: string;
  list: {
    label: React.ReactNode;
    show: boolean;
    icon?: boolean;
    tooltipText?: string;
    values: {
      [k: string]: ReactNode;
    };
  }[];
}

export const TableRowTitle = styled.td`
  border-top: 1px solid ${colors.greyLt};
  ${fontSize("11px")}
  color: ${colors.grey};
  ${fontFamily(product_sans.medium)};
  text-transform: uppercase;
  padding-bottom: 36px;
  text-align: left;

  ${mediaQuery("<", "tablet")`
    padding-top: 30px;
    padding-bottom: 20px;
  `}
`;

export const TableFootRow = styled.tr`
  td {
    border-top: 1px solid ${colors.greyLt};
    ${fontFamily(product_sans.medium)};
    padding: 30px 0;
  }
`;

export const HeaderTh = styled.th`
  ${fontFamily(product_sans.medium)};
  ${fontSize("12px")};
  font-weight: normal;
  color: ${colors.greyDrk};
  text-align: center;
  padding: 20px 0;
  z-index: 2;
  flex: 1;

  &:first-child {
    width: 270px;
    flex: inherit;
  }

  ${mediaQuery("<", "tablet")`
    top: 0;
    position: relative;
  `}

  .price {
    ${fontSize("30px")};
    position: relative;
    margin-right: 5px;
  }

  .numberOfFiles {
    color: ${colors.grey};
    margin-bottom: 30px;
  }

  .euro {
    position: absolute;
    ${fontSize("14px")}
    top: 4px;
    margin: 0;
  }

  .priceMonths {
    margin-top: 15px;
  }
`;

const CustomTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${fontSize("14px")};
  position: relative;
  text-align: center;
  table-layout: fixed;

  td:first-child {
    width: 270px;
  }

  tr:first-child td {
    border: none;
  }

  &:after {
    content: "";
    width: calc(100% - 270px);
    max-width: 780px;
    background: #fff;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
    box-shadow: 0 5px 30px 0px rgba(27, 22, 51, 0.05);
    border-radius: 10px;
  }
`;

const TooltipBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 30px;
`;

const FixedHeadTable = styled.div`
  position: absolute;
  width: 100%;
  max-width: 1050px;
  margin: 0 auto;
  z-index: 2;
`;

const Bolder = styled.span`
  font-family: product_sans-semibold;
`;

export const TablePerMonth = ({ children }: { children: React.ReactNode }) => (
  <div>
    <Bolder>{children}</Bolder> /mois
  </div>
);

export const HiddenTableRow = ({
  visible,
  forMobile,
  ...rest
}: {
  visible: boolean;
  forMobile?: boolean;
  children: ReactNode;
}) => (
  <motion.div
    style={{ overflow: "hidden" }}
    animate={
      visible
        ? { height: "auto", opacity: 1, marginBottom: forMobile ? 20 : 36 }
        : { height: 0, opacity: 0, marginBottom: 0 }
    }
    transition={{
      duration: 0.5,
      ease: "backInOut",
    }}
    initial={false}
    {...rest}
  />
);

export const checkedIcon = (
  <IconCheck20 fill={colors.green} width="20" height="20" />
);
export const uncheckedIcon = (
  <IconCross20 fill={colors.red} width="20" height="20" />
);

interface TablePriceProps {
  type: TablePriceType[];
  rows: TablePriceRow[];
}

const useMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
};

const TableStickContainer = ({ children }: { children: ReactNode }) => {
  return (
    <div
      style={{
        position: "sticky",
        top: "111px",
        zIndex: 2,
        marginBottom: -20,
        height: 100,
      }}
    >
      <FixedHeadTable>{children}</FixedHeadTable>
    </div>
  );
};

const Soon = styled.div`
  ${fontSize("12px")}
  line-height: 18px;
  color: ${colors.grey};
  margin-top: 30px;
  text-align: center;
`;

export const TablePrice = (props: TablePriceProps) => {
  const mounted = useMounted();

  if (!mounted) {
    return <TablePriceView {...props} />;
  }

  return (
    <>
      <TableStickContainer>
        <TablePriceView {...props} noFooter rows={[]} />
      </TableStickContainer>
      <TablePriceView {...props} noHeader />
      <Soon>
        * Ces fonctionnalités seront disponibles ultérieurement sur
        l'application.
      </Soon>
    </>
  );
};

const TablePriceView = ({
  type,
  rows,
  noFooter = true,
  noHeader = false,
}: TablePriceProps & { noFooter?: boolean; noHeader?: boolean }) => {
  const [isHidden, setIsHidden] = useState(false);

  return (
    <CustomTable>
      {!noHeader && (
        <thead>
          <tr>
            <HeaderTh />
            {type.map((item, i) => (
              <HeaderTh key={i}>
                <Title htmlLevel={3} theme="titleTableStyle">
                  {item.title}
                </Title>
                <div>
                  <span className="price">
                    {item.prixDossier}
                    <span className="euro">€</span>
                  </span>
                  /mois
                </div>
              </HeaderTh>
            ))}
          </tr>
        </thead>
      )}

      <tbody>
        {rows.map((item, i) => (
          <Fragment key={i}>
            <tr>
              <TableRowTitle
                style={{ paddingTop: !item.title ? 0 : 36 }}
                colSpan={4}
              >
                {item.title}
              </TableRowTitle>
            </tr>

            {item.list.map((element, j) => (
              <tr key={j}>
                <td>
                  <HiddenTableRow visible={element.show || isHidden}>
                    <TooltipBox>
                      {element.label}
                      {element.icon === true && (
                        <Tooltip
                          tooltipMessage={element.tooltipText}
                          dependencies={[isHidden]}
                        />
                      )}
                    </TooltipBox>
                  </HiddenTableRow>
                </td>
                {type.map((header) => (
                  <td key={header.key}>
                    <HiddenTableRow visible={element.show || isHidden}>
                      {element.values[header.key] === true
                        ? checkedIcon
                        : element.values[header.key] === false
                        ? uncheckedIcon
                        : element.values[header.key]}
                    </HiddenTableRow>
                  </td>
                ))}
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
      {!noFooter && (
        <tfoot>
          <TableFootRow>
            <td></td>
            <td>
              <Link
                to="price"
                spy={true}
                smooth={true}
                duration={700}
                offset={-100}
                onClick={showExtraRows}
              >
                <ArrowLink justifyContent="center">
                  {!isHidden ? "En voir plus" : "En voir moins"}
                </ArrowLink>
              </Link>
            </td>
            <td>
              <Link
                to="price"
                spy={true}
                smooth={true}
                duration={700}
                offset={-100}
                onClick={showExtraRows}
              >
                <ArrowLink justifyContent="center">
                  {!isHidden ? "En voir plus" : "En voir moins"}
                </ArrowLink>
              </Link>
            </td>
            <td>
              <Link
                to="price"
                spy={true}
                smooth={true}
                duration={700}
                offset={-100}
                onClick={showExtraRows}
              >
                <ArrowLink justifyContent="center">
                  {!isHidden ? "En voir plus" : "En voir moins"}
                </ArrowLink>
              </Link>
            </td>
          </TableFootRow>
        </tfoot>
      )}
    </CustomTable>
  );

  function showExtraRows() {
    setIsHidden(!isHidden);
  }
};
